import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import GeneralForm from '@/components/pages/create-webinar/config/GeneralForm';
import DiffusionForm from '@/components/pages/create-webinar/config/DiffusionForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  duplicateWebinar,
  fetchEvergreenConfiguration,
  fetchWebinarById,
  fetchWebinarSessionConfiguration,
  getWebinarSession,
  listSpeakers,
  saveEvergreenSources,
  updateWebinarSessionConfiguration,
} from '@/features/webinar/webinarThunks';
import { clearCurrentWebinar } from '@/features/webinar/webinarSlice';
import type { EvergreenInteractionSourceInput, WebinarLanguage } from '@/features/webinar/types';
import EvergreenMp4UploadDialog, {
  type EvergreenMp4UploadOutcome,
} from '@/components/pages/create-webinar/config/EvergreenMp4UploadDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { LanguageFlag } from '@/components/ui/FlagIcon';
import { APP_LANGUAGES } from '@/i18n/languages';
import type {
  PendingSessionCreationPayload,
  PendingSessionCreationState,
} from '@/components/pages/create-webinar/config/pendingSessionCreation';
import SpeakersForm from '@/components/pages/create-webinar/config/SpeakersForm';
import ModeratorsForm from '@/components/pages/create-webinar/config/ModeratorForm';
import SplashScreen from '@/components/common/SplashScreen';
import WebinarSessionForm from '@/components/pages/create-webinar/config/WebinarSessionForm';
import PresenterForm from '@/components/pages/create-webinar/config/PresenterForm';
import { resolveEvergreenConvertErrorKey } from '@/lib/evergreenConvertError';

function postCreateAccordionQuery(pendingState: PendingSessionCreationState | null): string {
  return pendingState?.type === 'EVERGREEN' ? 'openModerators=true' : 'openPresenters=true';
}

export default function ConfigurationSection() {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const { currentWebinar, currentSessionConfiguration, presenters, moderators, speakers } =
    useAppSelector((state) => state.webinar);
  const nonGeneralSectionsDisabled = !currentWebinar;
  const [isDuplicating, setIsDuplicating] = useState(false);
  const processedDuplicateId = useRef<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string>('item-1');
  const [selectedLanguage, setSelectedLanguage] = useState<WebinarLanguage>(
    currentWebinar?.language ?? 'FRENCH',
  );
  const [sessionStepValidated, setSessionStepValidated] = useState(false);
  const [generalCreationPrefill, setGeneralCreationPrefill] = useState<{
    title: string;
    subtitle: string;
    description: string;
  } | null>(null);
  const pendingSessionAfterCreateRef = useRef<PendingSessionCreationState | null>(null);
  const [pendingSessionDraft, setPendingSessionDraft] =
    useState<PendingSessionCreationState | null>(null);
  const [mp4Upload, setMp4Upload] = useState<{
    webinarId: number;
    file: File;
    waitForConversion: boolean;
    interactionSource: EvergreenInteractionSourceInput;
  } | null>(null);
  const mp4UploadResolverRef = useRef<((outcome: EvergreenMp4UploadOutcome) => void) | null>(null);
  const prevLocationRef = useRef(location.pathname);

  const webinarId = webinarIdParam;
  const duplicateWebinarId = searchParams.get('duplicateWebinarId');
  const isCreatingWithoutWebinar = !webinarId;
  const generalAccordionDisabled = isCreatingWithoutWebinar && !sessionStepValidated;

  const webinarPresenters = presenters.filter(
    (s) => currentWebinar?.id && s.webinarId === currentWebinar.id,
  );
  const webinarModerators = moderators.filter(
    (m) => currentWebinar?.id && m.webinarId === currentWebinar.id,
  );
  const webinarSpeakers = speakers.filter(
    (s) => currentWebinar?.id && s.webinarId === currentWebinar.id,
  );
  const hasPresenters = webinarPresenters.length > 0;
  const hasModerators = webinarModerators.length > 0;
  const hasSpeakers = webinarSpeakers.length > 0;

  const isEvergreenFromConfig = currentSessionConfiguration?.type === 'EVERGREEN';
  const isEvergreenCreationDraft = pendingSessionDraft?.type === 'EVERGREEN';
  const hidePresentersAndSpeakersSections =
    isEvergreenFromConfig || (isCreatingWithoutWebinar && isEvergreenCreationDraft);
  const isYouTube = currentWebinar?.transmissionMode === 'YOUTUBE';
  const youtubeTransmissionAllowed = useAppSelector(
    (state) => state.billing.usage.data?.youtubeTransmissionAllowed ?? false,
  );
  // Section Diffusion réservée aux plans éligibles (ou webinaire déjà en YouTube), masquée en evergreen.
  const showDiffusionSection =
    (youtubeTransmissionAllowed || isYouTube) && !hidePresentersAndSpeakersSections;

  const handleGeneralSuccess = useCallback(() => {
    setTimeout(() => {
      if (currentSessionConfiguration?.type === 'EVERGREEN') {
        setOpenAccordion('item-4');
      } else {
        setOpenAccordion(showDiffusionSection ? 'item-diffusion' : 'item-3');
      }
    }, 100);
  }, [currentSessionConfiguration?.type, showDiffusionSection]);

  const handleDiffusionSuccess = useCallback(() => {
    setTimeout(() => {
      setOpenAccordion('item-3');
    }, 100);
  }, []);

  const handleSessionCreationValidated = useCallback((payload: PendingSessionCreationPayload) => {
    if (payload.type === 'EVERGREEN') {
      setGeneralCreationPrefill(payload.prefill);
      const next: PendingSessionCreationState = {
        type: 'EVERGREEN',
        nextDates: payload.nextDates,
        sessions: payload.sessions,
        source: payload.source,
      };
      pendingSessionAfterCreateRef.current = next;
      setPendingSessionDraft(next);
    } else {
      setGeneralCreationPrefill(null);
      const next: PendingSessionCreationState = {
        type: 'LIVE',
        nextDates: payload.nextDates,
        sessions: payload.sessions,
      };
      pendingSessionAfterCreateRef.current = next;
      setPendingSessionDraft(next);
    }
    setSessionStepValidated(true);
    setOpenAccordion('item-2');
  }, []);

  const handleCreateComplete = useCallback(
    async (newWebinarId: number) => {
      if (!workspaceId) {
        const pendingNav = pendingSessionAfterCreateRef.current;
        navigate(
          `/create-webinar/configuration/${newWebinarId}?${postCreateAccordionQuery(pendingNav)}`,
          { replace: true },
        );
        return;
      }

      const pending = pendingSessionAfterCreateRef.current;
      if (!pending) {
        navigate(
          `/create-webinar/configuration/${newWebinarId}?${postCreateAccordionQuery(null)}`,
          { replace: true },
        );
        return;
      }

      try {
        if (pending.type === 'LIVE') {
          await dispatch(
            updateWebinarSessionConfiguration({
              workspaceId,
              webinarId: newWebinarId,
              input: { type: 'LIVE', sessions: pending.sessions },
            }),
          ).unwrap();

          const webinar = await dispatch(
            fetchWebinarById({ workspaceId, id: newWebinarId }),
          ).unwrap();
          if (webinar.nextSessionId) {
            await dispatch(
              getWebinarSession({
                workspaceId,
                webinarId: newWebinarId,
                sessionId: webinar.nextSessionId,
              }),
            ).unwrap();
          }
          await dispatch(
            fetchWebinarSessionConfiguration({ workspaceId, webinarId: newWebinarId }),
          ).unwrap();
        } else if (pending.source.video.kind === 'MP4') {
          const videoFile = pending.source.video.file;
          const interactionSource = pending.source.interactions;
          // Enregistrée avant l'envoi : un échec arrête l'import sans laisser une vidéo orpheline.
          await dispatch(
            updateWebinarSessionConfiguration({
              workspaceId,
              webinarId: newWebinarId,
              input: {
                type: 'EVERGREEN',
                sessions: pending.sessions.length > 0 ? pending.sessions : null,
              },
            }),
          ).unwrap();
          const outcome = await new Promise<EvergreenMp4UploadOutcome>((resolve) => {
            mp4UploadResolverRef.current = resolve;
            setMp4Upload({
              webinarId: newWebinarId,
              file: videoFile,
              waitForConversion: interactionSource.interactionSourceType === 'LIVE',
              interactionSource,
            });
          });
          mp4UploadResolverRef.current = null;
          setMp4Upload(null);

          await Promise.all([
            dispatch(fetchWebinarById({ workspaceId, id: newWebinarId })),
            dispatch(fetchWebinarSessionConfiguration({ workspaceId, webinarId: newWebinarId })),
            dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: newWebinarId })),
          ]);

          pendingSessionAfterCreateRef.current = null;
          setPendingSessionDraft(null);
          setGeneralCreationPrefill(null);
          setSessionStepValidated(false);

          if (outcome === 'failed' || outcome === 'cancelled') {
            toast.warning(t('sessionForm.mp4.createdWithoutVideo'));
            navigate(`/create-webinar/configuration/${newWebinarId}`, { replace: true });
            return false;
          }
          if (outcome === 'conversionBlocked') {
            navigate(`/create-webinar/evergreen/${newWebinarId}`, { replace: true });
            return true;
          }
          if (outcome === 'conversionPending') {
            toast.info(t('sessionForm.mp4.conversionPending'));
          }
          navigate(
            `/create-webinar/configuration/${newWebinarId}?${postCreateAccordionQuery(pending)}`,
            { replace: true },
          );
          return true;
        } else {
          const interactionSource = pending.source.interactions;
          const conversion = await dispatch(
            saveEvergreenSources({
              workspaceId,
              webinarId: newWebinarId,
              input: {
                videoSourceType: 'LIVE',
                videoSourceSessionId: pending.source.video.sourceSessionId,
                ...interactionSource,
              },
              awaitConversion: interactionSource.interactionSourceType === 'LIVE',
            }),
          ).unwrap();

          await dispatch(
            updateWebinarSessionConfiguration({
              workspaceId,
              webinarId: newWebinarId,
              input: {
                type: 'EVERGREEN',
                sessions: pending.sessions.length > 0 ? pending.sessions : null,
              },
            }),
          ).unwrap();

          await dispatch(fetchWebinarById({ workspaceId, id: newWebinarId })).unwrap();
          await dispatch(
            fetchWebinarSessionConfiguration({ workspaceId, webinarId: newWebinarId }),
          ).unwrap();

          // Vidéo plus courte que les interactions reprises : à corriger depuis l'étape Evergreen.
          if (conversion?.kind === 'blocked') {
            pendingSessionAfterCreateRef.current = null;
            setPendingSessionDraft(null);
            setGeneralCreationPrefill(null);
            setSessionStepValidated(false);
            navigate(`/create-webinar/evergreen/${newWebinarId}`, { replace: true });
            return true;
          }
          if (conversion?.kind === 'pending') {
            toast.info(t('sessionForm.mp4.conversionPending'));
          }
        }

        pendingSessionAfterCreateRef.current = null;
        setPendingSessionDraft(null);
        setGeneralCreationPrefill(null);
        setSessionStepValidated(false);
        navigate(
          `/create-webinar/configuration/${newWebinarId}?${postCreateAccordionQuery(pending)}`,
          { replace: true },
        );
      } catch (e) {
        console.error(e);
        toast.error(tCommon(resolveEvergreenConvertErrorKey(e), 'Une erreur est survenue.'));
        navigate(`/create-webinar/configuration/${newWebinarId}`, { replace: true });
        return false;
      }
    },
    [workspaceId, dispatch, navigate, t, tCommon],
  );

  const handleMp4UploadFinished = useCallback((outcome: EvergreenMp4UploadOutcome) => {
    mp4UploadResolverRef.current?.(outcome);
  }, []);

  const handleSessionSuccess = useCallback(() => {
    setTimeout(() => {
      setOpenAccordion(currentSessionConfiguration?.type === 'EVERGREEN' ? 'item-4' : 'item-3');
    }, 100);
  }, [currentSessionConfiguration?.type]);

  // Présentateur sauvegardé → ouvre Modérateur.
  const handlePresentersSuccess = useCallback(() => {
    setTimeout(() => {
      setOpenAccordion('item-4');
    }, 100);
  }, []);

  // Modérateur sauvegardé → ouvre Invité (flux LIVE) ou file vers Inscription (evergreen, section Invité masquée).
  const handleModeratorsSuccess = useCallback(() => {
    // Pas de section Invités (Evergreen ou YouTube) : les modérateurs sont la dernière étape avant l'inscription.
    if (hidePresentersAndSpeakersSections || isYouTube) {
      if (currentWebinar?.id) {
        navigate(`/create-webinar/signin/${currentWebinar.id}`);
      }
      return;
    }
    setTimeout(() => {
      setOpenAccordion('item-5');
    }, 100);
  }, [hidePresentersAndSpeakersSections, isYouTube, currentWebinar?.id, navigate]);

  // Invité sauvegardé → dernière sous-section, on passe à l'étape Inscription.
  const handleSpeakersSuccess = useCallback(() => {
    if (currentWebinar?.id) {
      navigate(`/create-webinar/signin/${currentWebinar.id}`);
    }
  }, [currentWebinar?.id, navigate]);

  useEffect(() => {
    const handleDuplicate = async () => {
      if (!duplicateWebinarId || !workspaceId) return;

      if (processedDuplicateId.current === duplicateWebinarId) return;

      const sourceId = parseInt(duplicateWebinarId, 10);
      if (isNaN(sourceId)) return;

      processedDuplicateId.current = duplicateWebinarId;
      setIsDuplicating(true);

      try {
        const newWebinarId = await dispatch(
          duplicateWebinar({
            workspaceId,
            sourceWebinarId: sourceId,
          }),
        ).unwrap();

        dispatch(clearCurrentWebinar());
        navigate(`/create-webinar/configuration/${newWebinarId}`, { replace: true });
        setIsDuplicating(false);
        processedDuplicateId.current = null;
      } catch (error) {
        console.error('Erreur lors de la duplication du webinar:', error);
        const err = error as { message?: string } | undefined;
        toast.error(err?.message ? tCommon(err.message, err.message) : tCommon('errors.generic'));
        navigate('/webinars', { replace: true });
        setIsDuplicating(false);
        processedDuplicateId.current = null;
      }
    };

    handleDuplicate();
  }, [duplicateWebinarId, workspaceId, dispatch, setSearchParams, navigate, tCommon]);

  useEffect(() => {
    if (webinarId && workspaceId) {
      const id = parseInt(webinarId, 10);
      if (!isNaN(id)) {
        dispatch(listSpeakers({ workspaceId, webinarId: id }));
      }
    }
  }, [webinarId, workspaceId, dispatch]);

  useEffect(() => {
    const openModerators = searchParams.get('openModerators');
    if (openModerators === 'true' && currentWebinar) {
      const timer = setTimeout(() => {
        setOpenAccordion('item-4');
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('openModerators');
        setSearchParams(newSearchParams, { replace: true });
      }, 300);
      return () => clearTimeout(timer);
    }

    const openPresenters = searchParams.get('openPresenters');
    if (openPresenters === 'true' && currentWebinar) {
      const timer = setTimeout(() => {
        if (currentSessionConfiguration?.type === 'EVERGREEN') {
          setOpenAccordion('item-4');
        } else {
          setOpenAccordion(showDiffusionSection ? 'item-diffusion' : 'item-3');
        }
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('openPresenters');
        setSearchParams(newSearchParams, { replace: true });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    searchParams,
    currentWebinar,
    currentSessionConfiguration?.type,
    showDiffusionSection,
    setSearchParams,
  ]);

  useEffect(() => {
    if (
      hidePresentersAndSpeakersSections &&
      (openAccordion === 'item-3' || openAccordion === 'item-5')
    ) {
      setOpenAccordion('item-4');
    } else if (isYouTube && openAccordion === 'item-5') {
      setOpenAccordion('item-4');
    }
  }, [hidePresentersAndSpeakersSections, isYouTube, openAccordion]);

  useEffect(() => {
    const isConfigurationPage = location.pathname.startsWith('/create-webinar/configuration');
    const wasOnConfigurationPage = prevLocationRef.current.startsWith(
      '/create-webinar/configuration',
    );

    if (
      isConfigurationPage &&
      !wasOnConfigurationPage &&
      prevLocationRef.current !== location.pathname
    ) {
      setOpenAccordion('');
    }

    prevLocationRef.current = location.pathname;
  }, [location.pathname]);

  if (duplicateWebinarId || isDuplicating) {
    return <SplashScreen label={t('configuration.creatingWebinar')} fullscreen />;
  }

  return (
    <TitleLayout
      title={t('configuration.title')}
      subtitle={t('configuration.subtitle')}
      description={t('configuration.generalDescription')}
    >
      <div className="space-y-1">
        <Label htmlFor="webinar-language" className="text-sm font-medium">
          {t('generalForm.language')}
        </Label>
        <p className="text-sm text-secondary">{t('generalForm.languageDescription')}</p>
        <Select
          value={selectedLanguage}
          onValueChange={(v) => setSelectedLanguage(v as WebinarLanguage)}
        >
          <SelectTrigger id="webinar-language" className="w-full mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {APP_LANGUAGES.map(({ code, apiValue }) => (
              <SelectItem key={code} value={apiValue}>
                <span className="flex items-center gap-2">
                  <LanguageFlag code={code} className="w-5 h-3.5 rounded-sm shrink-0" />
                  {t(`generalForm.languages.${code}`)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Accordion
        type="single"
        collapsible
        className="w-full"
        value={openAccordion}
        onValueChange={(value) => setOpenAccordion(value || '')}
      >
        <AccordionItem value="item-1">
          <AccordionTrigger fillForm={!!currentSessionConfiguration}>
            {t('configuration.session')}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 text-balance">
            <WebinarSessionForm
              webinar={currentWebinar}
              onSuccess={handleSessionSuccess}
              onSessionCreationValidated={
                isCreatingWithoutWebinar ? handleSessionCreationValidated : undefined
              }
              pendingSessionDraft={isCreatingWithoutWebinar ? pendingSessionDraft : null}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-2">
          <AccordionTrigger
            disabled={generalAccordionDisabled}
            aria-disabled={generalAccordionDisabled}
            fillForm={!!currentWebinar}
          >
            {t('configuration.general')}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 text-balance">
            <GeneralForm
              webinar={currentWebinar}
              onSuccess={handleGeneralSuccess}
              creationPrefill={generalCreationPrefill}
              creationFlowType={
                isCreatingWithoutWebinar && sessionStepValidated
                  ? (pendingSessionDraft?.type ?? null)
                  : null
              }
              onCreateComplete={
                isCreatingWithoutWebinar && sessionStepValidated ? handleCreateComplete : undefined
              }
              language={selectedLanguage}
            />
          </AccordionContent>
        </AccordionItem>

        {showDiffusionSection && (
          <AccordionItem value="item-diffusion">
            <AccordionTrigger
              disabled={nonGeneralSectionsDisabled}
              aria-disabled={nonGeneralSectionsDisabled}
              fillForm={!!currentWebinar}
            >
              {t('configuration.diffusion')}
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <DiffusionForm webinar={currentWebinar} onSuccess={handleDiffusionSuccess} />
            </AccordionContent>
          </AccordionItem>
        )}

        {!hidePresentersAndSpeakersSections && (
          <AccordionItem value="item-3">
            <AccordionTrigger
              disabled={nonGeneralSectionsDisabled}
              aria-disabled={nonGeneralSectionsDisabled}
              fillForm={hasPresenters}
            >
              {t('configuration.presenters')}
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <PresenterForm webinar={currentWebinar} onSuccess={handlePresentersSuccess} />
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="item-4">
          <AccordionTrigger
            disabled={nonGeneralSectionsDisabled}
            aria-disabled={nonGeneralSectionsDisabled}
            fillForm={hasModerators}
          >
            {t('configuration.moderators')}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 text-balance">
            <ModeratorsForm webinar={currentWebinar} onSuccess={handleModeratorsSuccess} />
          </AccordionContent>
        </AccordionItem>
        {!hidePresentersAndSpeakersSections && !isYouTube && (
          <AccordionItem value="item-5">
            <AccordionTrigger
              disabled={nonGeneralSectionsDisabled}
              aria-disabled={nonGeneralSectionsDisabled}
              fillForm={hasSpeakers}
            >
              {t('configuration.speakers')}
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 text-balance">
              <SpeakersForm webinar={currentWebinar} onSuccess={handleSpeakersSuccess} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
      {mp4Upload && workspaceId && (
        <EvergreenMp4UploadDialog
          open
          workspaceId={workspaceId}
          webinarId={mp4Upload.webinarId}
          file={mp4Upload.file}
          waitForConversion={mp4Upload.waitForConversion}
          interactionSource={mp4Upload.interactionSource}
          onFinished={handleMp4UploadFinished}
        />
      )}
    </TitleLayout>
  );
}
